import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { ModalRefProps } from 'src/common/components/Modal';
import { OrbitIcons } from 'src/common/constants.';

import {
  InterstellarAccount,
  InterstellarUserRow,
  accountIsGeoreferenced,
  fetchAccountUsers,
  fetchCallModulesForAccount,
  fetchCallReports,
  deleteCallModule,
  updateAccountPhoneNumbers,
} from '../api';
import type { CallReport, ReportStatus } from '../api';
import { USER_ROLE_LABELS, getApiErrorMessage } from '../constants';
import { parsePhoneNumber, parseSipTrunk, toE164 } from '../utils';

import { AccountMap } from './AccountMap';
import type { AccountSectionId } from './AccountSidebar';
import { AddCallModuleModal } from './AddCallModuleModal';
import { AddUserModal } from './AddUserModal';
import { CallGroupManager } from './CallGroupManager';
import { CallModuleDetailView } from './CallModuleDetailView';
import { EditUserModal, EditUserModalRefProps } from './EditUserModal';
import { OfficeScheduleManager } from './OfficeScheduleManager';
import { PhoneAnalytics } from './PhoneAnalytics';
import { PlaybackAudioManager } from './PlaybackAudioManager';
import { ReportsDashboard } from './ReportsDashboard';
import { ResolutionStrategyTab } from './ResolutionStrategyTab';
import { RoutingGraph } from './RoutingGraph';
import { TabulationsDashboard } from './TabulationsDashboard';
import { UnavailableReasonsManager } from './UnavailableReasonsManager';

const REPORT_TYPE_LABELS: Record<string, string> = {
  call_quality: 'Calidad de llamada',
  interference: 'Interferencia',
  dropped_call: 'Llamada caída',
  echo: 'Eco',
  one_way_audio: 'Audio unidireccional',
  other: 'Otro',
};

const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  open: 'Abierto',
  in_progress: 'En progreso',
  resolved: 'Resuelto',
  dismissed: 'Descartado',
};

const REPORT_STATUS_COLORS: Record<ReportStatus, { bg: string; text: string }> = {
  open: { bg: 'bg-blue-100', text: 'text-blue-700' },
  in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  resolved: { bg: 'bg-green-100', text: 'text-green-700' },
  dismissed: { bg: 'bg-gray-100', text: 'text-gray-500' },
};

function formatReportDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-PR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function formatTel(value: string | number | null | undefined): string {
  if (value == null || value === '') return '';
  return String(value);
}

function userFirstName(u: InterstellarUserRow): string {
  if (u.firstName) return u.firstName;
  if (u.first_name) return u.first_name;
  if (u.username) return u.username;
  if (u.email) return u.email.split('@')[0];
  return '';
}

function userLastName(u: InterstellarUserRow): string {
  return u.lastName ?? u.last_name ?? '';
}

function userDisplayName(u: InterstellarUserRow): string {
  const first = userFirstName(u);
  const last = userLastName(u);
  const full = [first, last].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (u.cosmos_user_id) return `Usuario ${u.cosmos_user_id}`;
  return u.id != null ? `Extensión ${u.phone ?? u.id}` : 'Usuario';
}

function roleLabel(roleId: number | null | undefined): string {
  if (roleId == null) return 'Usuario';
  return USER_ROLE_LABELS[roleId] ?? 'Usuario';
}

const Section: React.FC<{
  title: string;
  icon: string;
  children: React.ReactNode;
  right?: React.ReactNode;
}> = ({ title, icon, children, right }) => (
  <section className="mb-6">
    <div className="flex items-center justify-between gap-2 mb-3">
      <h3 className="flex items-center gap-2.5 text-xs font-bold uppercase tracking-wide text-gray-3.0">
        <span className="flex h-8 w-8 items-center justify-center rounded bg-white-2.0">
          <Icon name={icon} size={16} className="text-primary-2.0" />
        </span>
        {title}
      </h3>
      {right}
    </div>
    <div className="overflow-hidden rounded border border-white-2.0 bg-white-0.0 shadow-sm">
      {children}
    </div>
  </section>
);

const InfoRow: React.FC<{
  icon: string;
  label: string;
  value?: string | null;
}> = ({ icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 border-b border-white-2.0 px-4 py-3 last:border-b-0">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded bg-white-1.0">
        <Icon name={icon} size={16} className="text-primary-2.0" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-2.0">{label}</p>
        <p className="text-sm font-semibold text-black-1.0">{value}</p>
      </div>
    </div>
  );
};

const PhoneHighlight: React.FC<{
  label: string;
  value: string;
  icon: string;
  editable?: boolean;
  editing?: boolean;
  editValue?: string;
  onEditStart?: () => void;
  onEditChange?: (value: string) => void;
  onEditSave?: () => void;
  onEditCancel?: () => void;
  saving?: boolean;
}> = ({
  label,
  value,
  icon,
  editable,
  editing,
  editValue,
  onEditStart,
  onEditChange,
  onEditSave,
  onEditCancel,
  saving,
}) => (
  <div className="flex gap-3 border-b border-white-2.0 px-4 py-3 last:border-b-0">
    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-white-2.0">
      <Icon name={icon} size={18} className="text-primary-2.0" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-2.0">{label}</p>
      {editing ? (
        <div className="flex items-center gap-2 mt-1">
          <input
            type="text"
            value={editValue ?? ''}
            onChange={(e) => onEditChange?.(e.target.value)}
            className="flex-1 rounded border border-white-2.0 px-2 py-1 font-mono text-sm text-black-1.0 focus:border-primary-1.0 focus:outline-none"
            disabled={saving}
            autoFocus
          />
          <button
            type="button"
            onClick={onEditSave}
            disabled={saving}
            className="rounded bg-primary-2.0 px-2 py-1 text-xs font-semibold text-white-0.0 hover:bg-primary-1.0 disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
          <button
            type="button"
            onClick={onEditCancel}
            disabled={saving}
            className="rounded border border-gray-1.0 px-2 py-1 text-xs font-semibold text-gray-2.0 hover:bg-gray-0.0 disabled:opacity-50"
          >
            Cancelar
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <p className="mt-0.5 font-mono text-base font-bold text-black-1.0">{value}</p>
          {editable && (
            <button
              type="button"
              onClick={onEditStart}
              className="rounded p-1 text-gray-2.0 transition-colors hover:bg-white-1.0 hover:text-primary-2.0"
              title={`Editar ${label.toLowerCase()}`}
            >
              <Icon name={OrbitIcons.Pencil} size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  </div>
);

const SipTrunkSection: React.FC<{ account: InterstellarAccount }> = ({ account }) => {
  const { trunks, proxy } = parseSipTrunk(account.sip_trunk);

  return (
    <Section title="SIP Trunk" icon={OrbitIcons.Cloud}>
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded bg-white-1.0">
          <Icon name={OrbitIcons.Cloud} size={16} className="text-primary-2.0" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-2.0">
            {trunks.length > 1 ? 'Troncales, el primero en uso' : 'Trunk'}
          </p>
          {trunks.length === 0 ? (
            <p className="text-sm font-semibold text-gray-2.0">Sin configurar</p>
          ) : (
            <ul>
              {trunks.map((trunk) => (
                <li key={trunk.name} className="text-sm font-semibold text-black-1.0">
                  {trunk.name}
                  {trunk.host && <span className="font-normal text-gray-2.0"> · {trunk.host}</span>}
                  {trunk.state && (
                    <span className="font-normal text-gray-2.0"> · {trunk.state}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          {proxy && <p className="mt-1 text-xs text-gray-2.0">Sale por {proxy}</p>}
        </div>
      </div>
    </Section>
  );
};

interface AccountDetailContentProps {
  account: InterstellarAccount;
  activeSection: AccountSectionId;
  placementMode: boolean;
  draftLatLng: [number, number] | null;
  savingGeolocation: boolean;
  onRequestGeoreference: (prefillFromAccount: boolean) => void;
  onCancelGeolocationPlacement: () => void;
  onSaveGeolocation: () => void;
  onDraftLatLngChange: (lat: number, lng: number) => void;
}

export const AccountDetailContent: React.FC<AccountDetailContentProps> = ({
  account,
  activeSection,
  placementMode,
  draftLatLng,
  savingGeolocation,
  onRequestGeoreference,
  onCancelGeolocationPlacement,
  onSaveGeolocation,
  onDraftLatLngChange,
}) => {
  const [expandedUserId, setExpandedUserId] = useState<number | string | null>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);
  const [phoneSubTab, setPhoneSubTab] = useState<
    'modules' | 'schedules' | 'groups' | 'audios' | 'reasons'
  >('modules');
  const addUserModalRef = useRef<ModalRefProps>(null);
  const editUserModalRef = useRef<EditUserModalRefProps>(null);
  const addCallModuleModalRef = useRef<ModalRefProps>(null);
  const queryCache = useQueryCache();

  // Phone number editing state
  const [editingMainLine, setEditingMainLine] = useState(false);
  const [editingRescueLine, setEditingRescueLine] = useState(false);
  const [editMainLineValue, setEditMainLineValue] = useState('');
  const [editRescueLineValue, setEditRescueLineValue] = useState('');

  const [updatePhoneNumbers, { isLoading: savingPhoneNumbers }] = useMutation(
    (payload: { new_phone_number?: string; new_call_rescue_number?: number }) =>
      updateAccountPhoneNumbers(account.id, payload),
    {
      onSuccess: () => {
        Toast.success('Número actualizado exitosamente');
        setEditingMainLine(false);
        setEditingRescueLine(false);
        queryCache.invalidateQueries(['interstellar-accounts']);
        queryCache.invalidateQueries(['interstellar-account', account.id]);
      },
      onError: (err: unknown) => {
        Toast.error(getApiErrorMessage(err, 'Error al actualizar el número'));
      },
    }
  );

  const [removeCallModule] = useMutation((moduleId: number) => deleteCallModule(moduleId), {
    onSuccess: () => {
      Toast.success('Módulo eliminado exitosamente');
      setSelectedModuleId(null);
      queryCache.invalidateQueries(['interstellar-call-modules', account.id]);
    },
    onError: () => {
      Toast.error('Error al eliminar el módulo');
    },
  });

  // Reset sub-states when account changes
  useEffect(() => {
    setExpandedUserId(null);
    setSelectedModuleId(null);
    setPhoneSubTab('modules');
    setEditingMainLine(false);
    setEditingRescueLine(false);
  }, [account.id]);

  const {
    data: usersPayload,
    isLoading: usersLoading,
    isError: usersError,
  } = useQuery(
    ['interstellar-account-users', account.id],
    () => fetchAccountUsers(account.id, { hydrate: false, pageSize: 200 }),
    {
      enabled: activeSection === 'usuarios',
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const {
    data: callModules = [],
    isLoading: modulesLoading,
    isError: modulesError,
  } = useQuery(
    ['interstellar-call-modules', account.id],
    () => fetchCallModulesForAccount(account.id),
    {
      enabled: activeSection === 'telefono',
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const { data: usersPayloadForPhone } = useQuery(
    ['interstellar-account-users-phone', account.id],
    () => fetchAccountUsers(account.id, { hydrate: false, pageSize: 200 }),
    {
      enabled: activeSection === 'telefono',
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const extensionPhones = useMemo(() => {
    const users = usersPayloadForPhone?.users ?? [];
    const set = new Set<string>();
    for (const u of users) {
      if (u.phone != null && String(u.phone).trim() !== '') {
        set.add(String(u.phone).trim());
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [usersPayloadForPhone]);

  const { data: userReportsPayload, isLoading: userReportsLoading } = useQuery(
    ['interstellar-user-reports', account.id, expandedUserId],
    () => {
      if (expandedUserId == null) {
        return Promise.reject(new Error('No user selected'));
      }
      return fetchCallReports(account.id, { userId: Number(expandedUserId), pageSize: 50 });
    },
    {
      enabled: expandedUserId != null && activeSection === 'usuarios',
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  const onMap = accountIsGeoreferenced(account);
  const fullAddress = [account.address, account.city, account.state, account.zip_code]
    .filter(Boolean)
    .join(', ');

  const coordText =
    onMap && account.latitude != null && account.longitude != null
      ? `${Number(account.latitude).toFixed(5)}, ${Number(account.longitude).toFixed(5)}`
      : null;

  const draftText = draftLatLng
    ? `${draftLatLng[0].toFixed(5)}, ${draftLatLng[1].toFixed(5)}`
    : null;

  const mainLine = formatTel(account.phone_number);
  const rescueLine = formatTel(account.call_rescue_number);

  const users = usersPayload?.users ?? [];
  const usersTotal = usersPayload?.total_count;

  return (
    <div className="flex h-full flex-col bg-white-1.0">
      {/* Content container */}
      <div
        className={classNames(
          'animate-in fade-in slide-in-from-bottom-4 flex-1 duration-300',
          activeSection === 'vivo'
            ? 'flex min-h-0 flex-col overflow-hidden'
            : 'overflow-auto px-5 py-6'
        )}
        key={activeSection}
      >
        {activeSection === 'resumen' && (
          <>
            <Section title="Contacto e índices" icon={OrbitIcons.Mail}>
              <div>
                <InfoRow icon={OrbitIcons.Mail} label="Email" value={account.email} />
                <InfoRow
                  icon={OrbitIcons.Search}
                  label="Search index"
                  value={account.search_index_name}
                />
                <InfoRow
                  icon={OrbitIcons.MapPin}
                  label="Dirección"
                  value={fullAddress || undefined}
                />
              </div>
            </Section>

            <SipTrunkSection account={account} />

            <Section title="Ubicación" icon={OrbitIcons.Map}>
              <div>
                <InfoRow icon={OrbitIcons.Globe} label="País" value={account.country} />
                <InfoRow icon={OrbitIcons.Clock} label="Zona horaria" value={account.timezone} />
                {coordText ? (
                  <InfoRow icon={OrbitIcons.Map} label="Coordenadas" value={coordText} />
                ) : (
                  <div className="flex items-start gap-3 px-4 py-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white-1.0">
                      <Icon name={OrbitIcons.Map} size={16} className="text-gray-2.0" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-gray-2.0">Coordenadas</p>
                      <p className="text-black-1.0 text-sm font-semibold">No georreferenciado</p>
                    </div>
                  </div>
                )}

                {placementMode ? (
                  <div className="border-t border-orange-1.0 bg-orange-2.0 p-4">
                    <p className="mb-3 text-xs font-semibold text-black-1.0">
                      Haz clic en el mapa para colocar el pin naranja, o ajusta las coordenadas
                      manualmente.
                    </p>
                    <div className="mb-4 h-64 overflow-hidden rounded-xl border border-orange-1.0">
                      <AccountMap
                        accounts={onMap ? [account] : []}
                        selectedAccount={onMap ? account : null}
                        onAccountSelect={() => {}}
                        placementMode
                        draftLatLng={draftLatLng}
                        onPlacementMapClick={onDraftLatLngChange}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="block">
                        <span className="text-black-2.0 mb-2 block text-xs font-bold uppercase tracking-wide">
                          Latitud
                        </span>
                        <input
                          type="number"
                          step="any"
                          value={draftLatLng?.[0] ?? ''}
                          onChange={(e) => {
                            const lat = parseFloat(e.target.value);
                            const lng = draftLatLng?.[1] ?? 18.22;
                            if (!Number.isNaN(lat)) {
                              onDraftLatLngChange(lat, lng);
                            }
                          }}
                          className="w-full rounded-xl border-2 border-white-2.0 bg-white px-3 py-2.5 font-mono text-sm font-medium text-black-1.0 shadow-sm transition-all focus:border-orange-1.0 focus:ring-2 focus:ring-white-2.0"
                          placeholder="18.22…"
                        />
                      </label>
                      <label className="block">
                        <span className="text-black-2.0 mb-2 block text-xs font-bold uppercase tracking-wide">
                          Longitud
                        </span>
                        <input
                          type="number"
                          step="any"
                          value={draftLatLng?.[1] ?? ''}
                          onChange={(e) => {
                            const lng = parseFloat(e.target.value);
                            const lat = draftLatLng?.[0] ?? 18.22;
                            if (!Number.isNaN(lng)) {
                              onDraftLatLngChange(lat, lng);
                            }
                          }}
                          className="w-full rounded-xl border-2 border-white-2.0 bg-white px-3 py-2.5 font-mono text-sm font-medium text-black-1.0 shadow-sm transition-all focus:border-orange-1.0 focus:ring-2 focus:ring-white-2.0"
                          placeholder="-66.59…"
                        />
                      </label>
                    </div>
                    {draftText && (
                      <div className="bg-white/60 px-3 py-2 mt-3 rounded-lg">
                        <p className="text-black-2.0 text-xs font-semibold">
                          Vista previa:{' '}
                          <span className="text-black-1.0 font-mono font-bold">{draftText}</span>
                        </p>
                      </div>
                    )}
                    <div className="mt-4 flex flex-wrap gap-2.5">
                      <button
                        type="button"
                        className="flex-1 rounded-xl border-2 border-orange-2.0 bg-white px-4 py-2.5 text-sm font-semibold text-black-2.0 shadow-sm transition-all hover:bg-orange-2.0 disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={savingGeolocation}
                        onClick={onCancelGeolocationPlacement}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="flex-1 rounded-xl bg-orange-1.0 px-4 py-2.5 text-sm font-bold text-white shadow-md transition-all hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={!draftLatLng || savingGeolocation}
                        onClick={onSaveGeolocation}
                      >
                        {savingGeolocation ? 'Guardando…' : 'Guardar en Interstellar'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="border-t border-white-2.0 px-4 py-3">
                    {!onMap ? (
                      <button
                        type="button"
                        className="flex w-full items-center justify-center gap-2 rounded bg-secondary-0.0 px-4 py-3 text-sm font-semibold text-white-0.0 transition-colors hover:bg-secondary-1.0"
                        onClick={() => onRequestGeoreference(false)}
                      >
                        <Icon name={OrbitIcons.MapPin} size={18} />
                        Georreferenciar cuenta en el mapa
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="flex w-full items-center justify-center gap-2 rounded border-2 border-primary-2.0 bg-white-0.0 px-4 py-3 text-sm font-semibold text-primary-2.0 transition-all hover:bg-primary-0.0 hover:text-white-0.0"
                        onClick={() => onRequestGeoreference(true)}
                      >
                        <Icon name={OrbitIcons.MapPin} size={18} />
                        Actualizar ubicación en el mapa
                      </button>
                    )}
                  </div>
                )}
              </div>
            </Section>

            <Section title="Actividad" icon={OrbitIcons.Calendar}>
              <div>
                <InfoRow
                  icon={OrbitIcons.Calendar}
                  label="Creada"
                  value={
                    account.created_at
                      ? new Date(account.created_at.replace(' ', 'T')).toLocaleDateString('es-PR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                      : undefined
                  }
                />
                {account.updated_at ? (
                  <InfoRow
                    icon={OrbitIcons.RefreshCw}
                    label="Actualizada"
                    value={new Date(account.updated_at.replace(' ', 'T')).toLocaleDateString(
                      'es-PR',
                      {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }
                    )}
                  />
                ) : null}
              </div>
            </Section>
          </>
        )}

        {activeSection === 'usuarios' && (
          <Section
            title="Equipo"
            icon={OrbitIcons.UserPlus}
            right={
              <div className="flex items-center gap-2">
                {usersTotal != null && (
                  <span className="text-xs font-medium text-gray-2.0">{usersTotal} en total</span>
                )}
                <button
                  type="button"
                  onClick={() => addUserModalRef.current?.show()}
                  className="flex items-center gap-1.5 rounded-md bg-primary-2.0 px-2.5 py-1.5 text-xs font-semibold text-white-0.0 transition-colors hover:bg-primary-1.0"
                  title="Agregar nuevo usuario"
                >
                  <Icon name={OrbitIcons.PlusCircle} size={14} />
                  Nuevo Usuario
                </button>
              </div>
            }
          >
            <div>
              {usersLoading && (
                <p className="px-4 py-6 text-center text-sm text-gray-2.0">Cargando usuarios…</p>
              )}
              {usersError && (
                <p className="px-4 py-6 text-center text-sm text-gray-2.0">
                  No se pudieron cargar los usuarios. Revisa permisos de la API o Interstellar.
                </p>
              )}
              {!usersLoading && !usersError && users.length === 0 && (
                <p className="px-4 py-6 text-center text-sm text-gray-2.0">
                  No hay usuarios en esta cuenta.
                </p>
              )}
              {!usersLoading &&
                !usersError &&
                users.map((u, idx) => {
                  const firstName = userFirstName(u);
                  const lastName = userLastName(u);
                  const active = u.is_active === true || u.is_active === 1;
                  const initial = (firstName || lastName || '?').charAt(0);
                  const uKey = u.id ?? u.cosmos_user_id ?? idx;
                  const isExpanded = expandedUserId === uKey;
                  const userReports = isExpanded ? userReportsPayload?.reports ?? [] : [];
                  return (
                    <div key={`${uKey}-${u.phone ?? ''}`}>
                      <div
                        role="button"
                        tabIndex={0}
                        className={classNames(
                          'flex w-full items-center gap-3 border-b border-white-2.0 px-4 py-3 text-left transition-colors hover:bg-white-1.0/60 cursor-pointer',
                          isExpanded && 'bg-white-1.0/40'
                        )}
                        onClick={() => setExpandedUserId(isExpanded ? null : uKey)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setExpandedUserId(isExpanded ? null : uKey);
                          }
                        }}
                      >
                        <div
                          className={classNames(
                            'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold',
                            active
                              ? 'bg-primary-2.0/15 text-primary-2.0'
                              : 'bg-gray-0.0 text-gray-2.0'
                          )}
                        >
                          {initial.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline gap-1.5">
                            <p className="truncate text-sm font-semibold text-black-1.0">
                              {firstName || '—'}
                            </p>
                            <p className="truncate text-sm font-medium text-gray-3.0">{lastName}</p>
                          </div>
                          <p className="mt-0.5 truncate text-xs font-medium text-gray-2.0">
                            {[u.phone ? `Ext. ${u.phone}` : null, u.email]
                              .filter(Boolean)
                              .join(' · ') ||
                              (u.cosmos_user_id ? `Cosmos ${u.cosmos_user_id}` : null)}
                          </p>
                        </div>
                        <div className="flex flex-col items-end flex-shrink-0 gap-1">
                          <span className="rounded bg-white-1.0 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-gray-3.0">
                            {roleLabel(u.role_id)}
                          </span>
                          {u.status ? (
                            <span
                              className="max-w-[5.5rem] truncate text-[10px] font-medium text-gray-2.0"
                              title={u.status}
                            >
                              FS: {u.status}
                            </span>
                          ) : null}
                          {account.freeswitch_group_id && (
                            <span
                              className={classNames(
                                'rounded px-2 py-0.5 text-[10px] font-semibold',
                                u.in_call_center !== false
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-orange-100 text-orange-700'
                              )}
                            >
                              {u.in_call_center !== false ? 'En cola' : 'Sin cola'}
                            </span>
                          )}
                          {!active && (
                            <span className="text-[10px] font-semibold uppercase text-red-1.0">
                              Inactivo
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            editUserModalRef.current?.showWithUser(u);
                          }}
                          className="flex-shrink-0 rounded p-2 text-gray-2.0 transition-colors hover:bg-primary-0.0 hover:text-primary-2.0"
                          title="Editar usuario"
                          aria-label="Editar usuario"
                        >
                          <Icon name={OrbitIcons.Pencil} size={16} />
                        </button>
                        <Icon
                          name={isExpanded ? OrbitIcons.ChevronRight : OrbitIcons.ChevronLeft}
                          size={16}
                          className={classNames(
                            'flex-shrink-0 transition-transform text-gray-2.0',
                            isExpanded && 'rotate-90'
                          )}
                        />
                      </div>

                      {isExpanded && (
                        <div className="border-b border-white-2.0 bg-white-1.0/50 px-4 py-3">
                          <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-3.0 flex items-center gap-1.5">
                            <Icon
                              name={OrbitIcons.FileText}
                              size={13}
                              className="text-primary-2.0"
                            />
                            Reportes de {userDisplayName(u)}
                          </p>
                          {userReportsLoading && (
                            <p className="text-xs text-gray-2.0 py-2">Cargando reportes…</p>
                          )}
                          {!userReportsLoading && userReports.length === 0 && (
                            <p className="text-xs text-gray-2.0 py-2">
                              Este usuario no tiene reportes.
                            </p>
                          )}
                          {!userReportsLoading && userReports.length > 0 && (
                            <div className="space-y-2">
                              {userReports.map((r: CallReport) => (
                                <div
                                  key={r.id}
                                  className="flex items-start gap-2.5 rounded border border-white-2.0 bg-white-0.0 px-3 py-2"
                                >
                                  <div className="flex-shrink-0 mt-0.5">
                                    <Icon
                                      name={
                                        r.report_value === 'positive'
                                          ? OrbitIcons.ThumbsUp
                                          : OrbitIcons.ThumbsDown
                                      }
                                      size={14}
                                      className={
                                        r.report_value === 'positive'
                                          ? 'text-green-600'
                                          : 'text-red-500'
                                      }
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-xs font-semibold text-black-1.0">
                                        {REPORT_TYPE_LABELS[r.report_type] ?? r.report_type}
                                      </span>
                                      <span
                                        className={classNames(
                                          'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase',
                                          r.report_value === 'positive'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-red-100 text-red-600'
                                        )}
                                      >
                                        {r.report_value === 'positive' ? 'Positivo' : 'Negativo'}
                                      </span>
                                      <span
                                        className={classNames(
                                          'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase',
                                          (
                                            REPORT_STATUS_COLORS[r.status] ??
                                            REPORT_STATUS_COLORS.open
                                          ).bg,
                                          (
                                            REPORT_STATUS_COLORS[r.status] ??
                                            REPORT_STATUS_COLORS.open
                                          ).text
                                        )}
                                      >
                                        {REPORT_STATUS_LABELS[r.status] ?? r.status}
                                      </span>
                                    </div>
                                    {r.note && (
                                      <p className="mt-1 text-xs text-gray-3.0 italic">
                                        "{r.note}"
                                      </p>
                                    )}
                                    {r.resolved_note && (
                                      <p className="mt-0.5 text-xs text-green-700">
                                        Resolución: {r.resolved_note}
                                      </p>
                                    )}
                                    <p className="mt-0.5 text-[10px] text-gray-2.0">
                                      {formatReportDate(r.created_at)}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </Section>
        )}

        {activeSection === 'telefono' && (
          <>
            <Section
              title="Líneas y extensiones"
              icon={OrbitIcons.Phone}
              right={
                extensionPhones.length > 0 ? (
                  <span className="text-xs font-medium text-gray-2.0">
                    {extensionPhones.length} ext.
                  </span>
                ) : null
              }
            >
              <div>
                {mainLine ? (
                  <PhoneHighlight
                    label="Línea principal"
                    value={mainLine}
                    icon={OrbitIcons.Phone}
                    editable
                    editing={editingMainLine}
                    editValue={editMainLineValue}
                    onEditStart={() => {
                      setEditMainLineValue(mainLine);
                      setEditingMainLine(true);
                    }}
                    onEditChange={setEditMainLineValue}
                    onEditSave={() => {
                      const e164 = toE164(editMainLineValue);
                      if (!e164) {
                        Toast.error('Ingresa un número de 10 dígitos');
                      } else if (e164 !== toE164(mainLine)) {
                        updatePhoneNumbers({ new_phone_number: e164 });
                      } else {
                        setEditingMainLine(false);
                      }
                    }}
                    onEditCancel={() => setEditingMainLine(false)}
                    saving={savingPhoneNumbers}
                  />
                ) : null}
                {rescueLine ? (
                  <PhoneHighlight
                    label="Call rescue"
                    value={rescueLine}
                    icon={OrbitIcons.PhoneIncoming}
                    editable
                    editing={editingRescueLine}
                    editValue={editRescueLineValue}
                    onEditStart={() => {
                      setEditRescueLineValue(rescueLine);
                      setEditingRescueLine(true);
                    }}
                    onEditChange={setEditRescueLineValue}
                    onEditSave={() => {
                      const e164 = toE164(editRescueLineValue);
                      if (!e164) {
                        Toast.error('Ingresa un número de 10 dígitos');
                      } else if (e164.slice(1) !== parsePhoneNumber(rescueLine)) {
                        updatePhoneNumbers({ new_call_rescue_number: Number(e164) });
                      } else {
                        setEditingRescueLine(false);
                      }
                    }}
                    onEditCancel={() => setEditingRescueLine(false)}
                    saving={savingPhoneNumbers}
                  />
                ) : null}
                {!mainLine && !rescueLine && (
                  <p className="px-4 py-4 text-sm text-gray-2.0">
                    Sin números de cuenta en Interstellar.
                  </p>
                )}
                {extensionPhones.length > 0 && (
                  <div className="border-t border-white-2.0 bg-white-0.0 px-4 py-3">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-2.0">
                      Extensiones SIP (usuarios)
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {extensionPhones.map((ext) => (
                        <span
                          key={ext}
                          className="inline-flex items-center rounded bg-primary-0.0 px-2.5 py-1 font-mono text-xs font-semibold text-white-0.0"
                        >
                          {ext}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Section>

            {/* Sub-tabs for phone configuration */}
            <div className="mb-4 flex gap-1 rounded-lg bg-white-1.0 p-1">
              {[
                { id: 'modules' as const, label: 'Módulos', icon: OrbitIcons.Phone },
                { id: 'schedules' as const, label: 'Horarios', icon: OrbitIcons.Calendar },
                { id: 'groups' as const, label: 'Grupos', icon: OrbitIcons.UserPlus },
                { id: 'audios' as const, label: 'Audios', icon: 'volume-2' },
                { id: 'reasons' as const, label: 'Motivos', icon: OrbitIcons.Clock },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setPhoneSubTab(tab.id);
                    setSelectedModuleId(null);
                  }}
                  className={classNames(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition-colors',
                    phoneSubTab === tab.id
                      ? 'bg-white-0.0 text-primary-2.0 shadow-sm'
                      : 'text-gray-2.0 hover:text-black-1.0'
                  )}
                >
                  <Icon name={tab.icon} size={14} />
                  {tab.label}
                </button>
              ))}
            </div>

            {phoneSubTab === 'modules' && !selectedModuleId && (
              <Section
                title="Módulos de llamada"
                icon={OrbitIcons.Settings}
                right={
                  <button
                    type="button"
                    onClick={() => addCallModuleModalRef.current?.show()}
                    className="btn btn-secondary btn-sm flex items-center gap-1"
                  >
                    <Icon name={OrbitIcons.PlusCircle} size={14} />
                    Crear módulo
                  </button>
                }
              >
                <div className="p-4">
                  {modulesLoading && (
                    <p className="text-sm text-gray-2.0">Cargando módulos de entrada…</p>
                  )}
                  {modulesError && (
                    <p className="text-sm text-gray-2.0">
                      No se pudieron cargar los módulos. Comprueba permisos de API o Interstellar.
                    </p>
                  )}
                  {!modulesLoading && !modulesError && callModules.length === 0 && (
                    <div className="rounded border border-dashed border-white-2.0 py-8 text-center">
                      <Icon
                        name={OrbitIcons.Phone}
                        size={32}
                        className="mx-auto mb-2 text-gray-1.0"
                      />
                      <p className="text-sm text-gray-2.0">No hay módulos configurados</p>
                      <p className="text-xs text-gray-1.0">Crea tu primer módulo de llamada</p>
                    </div>
                  )}
                  {!modulesLoading &&
                    !modulesError &&
                    callModules.map((mod) => (
                      <button
                        key={mod.id}
                        type="button"
                        onClick={() => setSelectedModuleId(mod.id)}
                        className="mb-2 flex w-full cursor-pointer items-center justify-between rounded border border-white-2.0 bg-white-1.0 px-3 py-2 text-left last:mb-0 hover:border-primary-1.0 hover:bg-primary-0.0/5"
                      >
                        <div>
                          <p className="text-sm font-semibold text-black-1.0">{mod.name}</p>
                          <p className="font-mono text-xs text-gray-2.0">{mod.phone_number}</p>
                        </div>
                        <Icon name={OrbitIcons.ChevronRight} size={16} className="text-gray-2.0" />
                      </button>
                    ))}
                </div>
              </Section>
            )}

            {phoneSubTab === 'modules' && selectedModuleId && (
              <Section title="Detalle del módulo" icon={OrbitIcons.Settings}>
                <CallModuleDetailView
                  moduleId={selectedModuleId}
                  onClose={() => setSelectedModuleId(null)}
                  onEdit={() => {
                    // TODO: implement edit modal
                  }}
                  onDelete={(moduleId) => removeCallModule(moduleId)}
                />
              </Section>
            )}

            {phoneSubTab === 'schedules' && (
              <Section title="Horarios de oficina" icon={OrbitIcons.Calendar}>
                <OfficeScheduleManager accountId={account.id} />
              </Section>
            )}

            {phoneSubTab === 'groups' && (
              <Section title="Grupos de agentes" icon={OrbitIcons.UserPlus}>
                <CallGroupManager accountId={account.id} />
              </Section>
            )}

            {phoneSubTab === 'audios' && (
              <Section title="Audios de reproducción" icon={'volume-2'}>
                <PlaybackAudioManager accountId={account.id} />
              </Section>
            )}

            {phoneSubTab === 'reasons' && (
              <Section title="Motivos de No disponible" icon={OrbitIcons.Clock}>
                <UnavailableReasonsManager accountId={account.id} />
              </Section>
            )}
          </>
        )}

        {activeSection === 'ruteo' && <RoutingGraph accountId={account.id} />}

        {activeSection === 'reportes' && <ReportsDashboard accountId={account.id} />}

        {activeSection === 'tabulaciones' && <TabulationsDashboard accountId={account.id} />}

        {activeSection === 'resoluciones' && <ResolutionStrategyTab accountId={account.id} />}

        {activeSection === 'vivo' && (
          <div className="bg-gray-50 flex flex-col flex-1 min-h-0">
            <PhoneAnalytics accountId={account.id} />
          </div>
        )}
      </div>

      {/* Modals */}
      <AddUserModal
        ref={addUserModalRef}
        accountId={account.id}
        cosmosAccountId={account.cosmos_account_id}
        isSharedAccount={!!account.freeswitch_group_id}
        onUserCreated={() => {
          queryCache.invalidateQueries(['interstellar-account-users', account.id]);
        }}
      />

      <EditUserModal
        ref={editUserModalRef}
        accountId={account.id}
        isSharedAccount={!!account.freeswitch_group_id}
        onUserUpdated={() => {
          queryCache.invalidateQueries(['interstellar-account-users', account.id]);
        }}
      />

      <AddCallModuleModal
        ref={addCallModuleModalRef}
        accountId={account.id}
        isFirstModule={!modulesLoading && !modulesError && callModules.length === 0}
        onModuleCreated={() => {
          queryCache.invalidateQueries(['interstellar-call-modules', account.id]);
        }}
      />
    </div>
  );
};
